import { forwardRef, useEffect, useId, useState } from "react";
import ArrowDropDown from "@servicetitan/hammer-icon/mdi/round/arrow_drop_down.svg";
import Listbox, { ListboxItemType } from "../../../components/Listbox";
import Popover from "../../../internal/components/Popover";
import { ToolbarItemAppearance } from "./types";
import { DataTrackingId } from "../../../types";
import { useOverFlowItemContext } from "./internal/ToolbarItemOverflowContext";
import { ToolbarItemWrapper } from "./internal/ToolbarItemWrapper";
import { useTrackingId } from "../../../hooks";
import { useToolbarSize } from "./internal/ToolbarContext";

import cx from "classnames";
import styles from "./Toolbar.module.scss";

/**
 * Type for items in the ToolbarSelect dropdown
 * @extends ItemType<T & { id: string }>
 */
type ToolbarSelectItem<T = Record<string, unknown>> = ListboxItemType<
  T & {
    id: string;
  }
>;

/**
 * Props for the ToolbarSelect component
 * @extends Omit<React.ComponentPropsWithoutRef<typeof Popover.Button>, "appearance" | "onChange">
 */
export type ToolbarSelectProps<T = Record<string, unknown>> = Omit<
  React.ComponentPropsWithoutRef<typeof Popover.Button>,
  "appearance" | "onChange"
> & {
  /**
   * Visual appearance of the select button
   * @default ghost
   */
  appearance?: ToolbarItemAppearance;
  /**
   * Callback function when selection changes
   */
  onChange?: (optionId: string) => void;
  /**
   * Currently selected item ID (controlled mode)
   */
  selected?: string;
  /**
   * Array of selectable items
   */
  items: ToolbarSelectItem<T>[];
  /**
   * Accessible label for the select dropdown dialog (e.g. "Status options").
   */
  accessibleLabel: string;
} & DataTrackingId;

/**
 * ToolbarSelect component provides a dropdown selector within a toolbar.
 *
 * Features:
 * - Dropdown menu with selectable options
 * - Controlled and uncontrolled usage patterns
 * - Automatic overflow menu integration
 * - Accessibility support with proper ARIA labels
 * - Ghost and primary appearance options
 * - Dropdown arrow icon indicator
 * - Listbox integration for keyboard navigation
 *
 * @example
 * <ToolbarSelect
 *   items={fontOptions}
 *   selected="arial"
 *   accessibleLabel="Font family"
 *   onChange={(id) => setFont(id)}
 * />
 *
 * @example
 * <ToolbarSelect
 *   appearance="primary"
 *   items={colorOptions}
 *   accessibleLabel="Text color"
 * />
 */
export const ToolbarSelect = forwardRef<HTMLButtonElement, ToolbarSelectProps>(
  (props, _ref) => {
    const {
      appearance = "ghost",
      onChange,
      selected: selectedItemId,
      accessibleLabel,
      className,
      items,
      ...rest
    } = props;
    const selectId = useId();

    // Popover open/close state - like TimeField does
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);

    // Internal state for uncontrolled mode
    const defaultId = items.length > 0 ? items[0].id : "";
    const [internalSelectedId, setInternalSelectedId] = useState(defaultId);

    // Use controlled value if provided, otherwise use internal state
    const selectedId = selectedItemId ?? internalSelectedId;

    // Find the selected item by ID (or default to first item using defaultId)
    const selectedItem =
      items.find((item) => item.id === selectedId) ||
      items.find((item) => item.id === defaultId);

    // Update internal selection when items change and current selection is no longer valid
    useEffect(() => {
      if (
        !selectedItemId &&
        items.length > 0 &&
        !items.some((item) => item.id === internalSelectedId)
      ) {
        setInternalSelectedId(items[0].id);
      }
    }, [items, selectedItemId, internalSelectedId]);

    // Handle selection change
    const handleSelectionChange = (
      selected: ToolbarSelectItem<Record<string, unknown>> | undefined,
    ) => {
      if (selected?.id) {
        if (selectedItemId === undefined) {
          setInternalSelectedId(selected.id);
        }

        onChange?.(selected.id);

        // Close the popover after selection
        setIsPopoverOpen(false);
      }
    };

    // Checks if this component is rendered inside the overflow menu and fallbacks when the context is unavailable
    const { isInOverflowMenu = false } = useOverFlowItemContext?.() || {};
    const toolbarSize = useToolbarSize();

    const trackingId = useTrackingId({
      name: "ToolbarSelect",
      data: {
        selected: props.selected,
        disabled: props.disabled,
        "aria-label": props["aria-label"],
        items: props.items,
      },
      hasOverride: !!props["data-tracking-id"],
    });

    const buttonClassNames = cx(className, styles["toolbar-button-item"]);

    const item = (
      <Popover
        noPadding
        open={isPopoverOpen}
        onClickOutside={() => {
          setIsPopoverOpen(false);
        }}
        onClose={() => {
          setIsPopoverOpen(false);
        }}
      >
        <Popover.Button
          data-tracking-id={trackingId}
          appearance={appearance}
          size={toolbarSize}
          icon={{ after: ArrowDropDown }}
          className={buttonClassNames}
          id={selectId}
          data-anv="toolbar-select"
          onClick={() => {
            setIsPopoverOpen(!isPopoverOpen);
          }}
          {...rest}
        >
          {selectedItem?.label}
        </Popover.Button>
        <Popover.Content
          className={styles["toolbar-select-content"]}
          aria-label={accessibleLabel}
        >
          <Listbox
            selected={selectedItem}
            onSelectionChange={handleSelectionChange}
            items={items}
            aria-labelledby={selectId}
            disableAutoSelectOnFocus={true}
          >
            {({ items }) =>
              items.map((item) => (
                <Listbox.Option key={item.label} item={item}>
                  {item.label}
                </Listbox.Option>
              ))
            }
          </Listbox>
        </Popover.Content>
      </Popover>
    );

    if (isInOverflowMenu) {
      return item;
    }

    return (
      <ToolbarItemWrapper item={{ itemProps: props, itemType: "select" }}>
        {item}
      </ToolbarItemWrapper>
    );
  },
);

ToolbarSelect.displayName = "ToolbarSelect";
